import React, { useState, useMemo } from "react";
import Select from "react-select";
import {
  IconArrowUpRight,
  IconBox,
  IconCalculator,
  IconCheck,
  IconRotate,
  IconAlertCircle,
  IconInfoCircle,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import { issueItems } from "../../controllers/item_issue.controller";
import toast from "react-hot-toast";

const DEPARTMENTS = [
  "Service / Waitstaff",
  "Bar & Beverages",
  "Kitchen / Chef",
  "Housekeeping",
  "Front Desk",
  "Store & Inventory",
  "Maintenance",
];

export default function IssueItemsView({
  eligibleItems,
  shifts,
  users,
  onIssueSuccess,
}) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [customDepartment, setCustomDepartment] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [selectedShift, setSelectedShift] = useState(null);
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split("T")[0]);
  const [sourceLocation, setSourceLocation] = useState("Main Store");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Format users for react-select
  const userOptions = useMemo(() => {
    return (users || []).map((u) => ({
      value: u.username,
      label: `${u.name || u.username} (${u.designation || u.role || "Staff"})`,
      user: u,
    }));
  }, [users]);

  // Format eligible items for react-select
  const itemOptions = useMemo(() => {
    return (eligibleItems || []).map((item) => {
      const avail = parseFloat(item.available_to_issue) || 0;
      return {
        value: item.id,
        label: `${item.title} — Available: ${avail.toLocaleString()} ${item.unit} (Custody: ${item.active_custody_base_qty || 0})`,
        item,
      };
    });
  }, [eligibleItems]);

  // When item changes, prepare its valid unit options (base unit + configured alternate units)
  const unitOptions = useMemo(() => {
    if (!selectedItem) return [];
    const item = selectedItem.item;
    const list = [
      {
        value: `base_${item.unit}`,
        label: `${item.unit} (Base Stock Unit - 1.0x)`,
        unit_name: item.unit,
        purchase_unit_id: null,
        conversion_factor: 1.0,
        is_base: true,
      },
    ];

    if (Array.isArray(item.alternate_units)) {
      item.alternate_units.forEach((u) => {
        list.push({
          value: `alt_${u.purchase_unit_id}`,
          label: `${u.unit_name} (${u.conversion_factor} ${item.unit} per ${u.unit_name})`,
          unit_name: u.unit_name,
          purchase_unit_id: u.purchase_unit_id,
          conversion_factor: parseFloat(u.conversion_factor) || 1.0,
          is_base: false,
        });
      });
    }

    return list;
  }, [selectedItem]);

  // Auto-select base unit when item is selected
  const handleItemChange = (opt) => {
    setSelectedItem(opt);
    if (opt && opt.item) {
      setSelectedUnit({
        value: `base_${opt.item.unit}`,
        label: `${opt.item.unit} (Base Stock Unit - 1.0x)`,
        unit_name: opt.item.unit,
        purchase_unit_id: null,
        conversion_factor: 1.0,
        is_base: true,
      });
    } else {
      setSelectedUnit(null);
    }
  };

  // Live conversion calculations
  const parsedQty = parseFloat(quantity) || 0;
  const factor = selectedUnit ? selectedUnit.conversion_factor : 1.0;
  const calculatedBaseQty = parsedQty * factor;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUser && !customDepartment) {
      toast.error("Please select an employee or specify a department.");
      return;
    }
    if (!selectedItem) {
      toast.error("Please select a product to issue.");
      return;
    }
    if (!selectedUnit) {
      toast.error("Please select an issue unit.");
      return;
    }
    if (parsedQty <= 0) {
      toast.error("Please enter a valid quantity greater than zero.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        user_id: selectedUser ? selectedUser.value : null,
        employee_name: selectedUser ? selectedUser.user.name : customDepartment,
        department: customDepartment || (selectedUser ? selectedUser.user.designation : null),
        inventory_item_id: selectedItem.item.id,
        quantity: parsedQty,
        purchase_unit_id: selectedUnit.purchase_unit_id,
        unit_name: selectedUnit.unit_name,
        shift_id: selectedShift ? selectedShift.value : null,
        shift_name: selectedShift ? selectedShift.label : null,
        source_location: sourceLocation,
        notes: notes,
        issue_date: issueDate,
      };

      const res = await issueItems(payload);
      if (res.data?.success) {
        toast.success(`Items successfully issued! Issue #${res.data.issue_number}`);
        // Reset form
        setQuantity("");
        setNotes("");
        if (onIssueSuccess) onIssueSuccess();
      } else {
        toast.error(res.data?.message || "Failed to issue items.");
      }
    } catch (err) {
      console.error("Issue item error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to issue items.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-base-100 p-6 rounded-2xl border border-base-200 shadow-sm max-w-4xl mx-auto">
      <div className="border-b border-base-200 pb-4 mb-6">
        <h2 className="text-xl font-bold text-base-content flex items-center gap-2">
          <IconArrowUpRight size={22} stroke={iconStroke} className="text-primary" />
          Issue Stock to Staff / Department
        </h2>
        <p className="text-xs text-base-content/60 mt-1">
          Allocate physical items to custodians. Stock remains accounted in warehouse and reserves custody without double-deduction.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Recipient Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Employee / Waiter / Staff
            </label>
            <Select
              className="text-sm react-select-container"
              classNamePrefix="react-select"
              options={userOptions}
              value={selectedUser}
              onChange={(opt) => {
                setSelectedUser(opt);
                if (opt && opt.user?.designation) {
                  setCustomDepartment(opt.user.designation);
                }
              }}
              placeholder="Search staff by name or username..."
              isClearable
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Department / Role
            </label>
            <input
              type="text"
              className="input input-sm input-bordered w-full text-sm"
              placeholder="e.g. Service / Waitstaff, Bar, Kitchen"
              value={customDepartment}
              onChange={(e) => setCustomDepartment(e.target.value)}
              list="dept-suggestions"
            />
            <datalist id="dept-suggestions">
              {DEPARTMENTS.map((d, i) => (
                <option key={i} value={d} />
              ))}
            </datalist>
          </div>
        </div>

        {/* Product & Unit Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Select Inventory Product <span className="text-error">*</span>
            </label>
            <Select
              className="text-sm react-select-container"
              classNamePrefix="react-select"
              options={itemOptions}
              value={selectedItem}
              onChange={handleItemChange}
              placeholder="Search product from inventory master..."
              isClearable
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Select Issue Unit <span className="text-error">*</span>
            </label>
            <Select
              className="text-sm react-select-container"
              classNamePrefix="react-select"
              options={unitOptions}
              value={selectedUnit}
              onChange={setSelectedUnit}
              isDisabled={!selectedItem}
              placeholder={selectedItem ? "Choose unit..." : "Select product first"}
            />
          </div>
        </div>

        {/* Quantity & Shift Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Issue Quantity <span className="text-error">*</span>
            </label>
            <input
              type="number"
              step="any"
              min="0.001"
              required
              className="input input-sm input-bordered w-full text-sm"
              placeholder="e.g. 2"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Shift
            </label>
            <select
              className="select select-sm select-bordered w-full text-sm"
              value={selectedShift ? selectedShift.value : ""}
              onChange={(e) => {
                const s = shifts?.find((sh) => String(sh.id) === e.target.value);
                setSelectedShift(s ? { value: s.id, label: s.name } : null);
              }}
            >
              <option value="">Default / Current Shift</option>
              {shifts?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.status || "Active"})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Date
            </label>
            <input
              type="date"
              className="input input-sm input-bordered w-full text-sm"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
            />
          </div>
        </div>

        {/* Live Calculation Preview Card */}
        {selectedItem && selectedUnit && parsedQty > 0 && (
          <div className="p-4 rounded-xl border bg-primary/5 border-primary/20 text-base-content">
            <div className="flex items-center gap-2 mb-2 font-bold text-sm">
              <IconCalculator size={18} stroke={iconStroke} />
              Unit Conversion & Custody Details
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="text-base-content/60 block">Entered Qty</span>
                <span className="font-bold text-sm">
                  {parsedQty} {selectedUnit.unit_name}
                </span>
              </div>
              <div>
                <span className="text-base-content/60 block">Conversion Factor</span>
                <span className="font-bold text-sm">
                  {factor} {selectedItem.item.unit} / {selectedUnit.unit_name}
                </span>
              </div>
              <div>
                <span className="text-base-content/60 block">Calculated Base Units</span>
                <span className="font-bold text-sm text-primary">
                  {calculatedBaseQty.toLocaleString(undefined, { maximumFractionDigits: 3 })}{" "}
                  {selectedItem.item.unit}
                </span>
              </div>
              <div>
                <span className="text-base-content/60 block">Custody Mode</span>
                <span className="font-bold text-sm text-success flex items-center gap-1">
                  <IconCheck size={14} /> Informational Tracking
                </span>
              </div>
            </div>

            <div className="mt-2 text-[11px] text-base-content/60 flex items-center gap-1">
              <IconInfoCircle size={14} stroke={iconStroke} />
              Item issue tracks staff borrowing and custody responsibility. It is purely informational and not associated with warehouse inventory or POS.
            </div>
          </div>
        )}

        {/* Source Location & Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Source Location / Store
            </label>
            <input
              type="text"
              className="input input-sm input-bordered w-full text-sm"
              value={sourceLocation}
              onChange={(e) => setSourceLocation(e.target.value)}
              placeholder="e.g. Main Warehouse, Bar Dispenser, Kitchen Store"
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Remarks / Purpose
            </label>
            <input
              type="text"
              className="input input-sm input-bordered w-full text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Evening shift issue, table service stock"
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-4 border-t border-base-200">
          <button
            type="submit"
            disabled={isSubmitting || parsedQty <= 0 || !selectedItem}
            className="btn btn-primary gap-2 px-8"
          >
            {isSubmitting ? (
              <IconRotate className="animate-spin" size={18} stroke={iconStroke} />
            ) : (
              <IconCheck size={18} stroke={iconStroke} />
            )}
            Confirm & Issue Items
          </button>
        </div>
      </form>
    </div>
  );
}
